
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import emotionDetectionService from '@/utils/emotionDetectionService';
import { getEmojiForEmotion } from '@/components/EmotionDetector';
import { ensureStorageBuckets } from '@/lib/storageUtils';

export const useVideoRecorder = () => {
  const mainVideoRef = useRef<HTMLVideoElement>(null);
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isReadyToRecord, setIsReadyToRecord] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedVideo, setRecordedVideo] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [currentEmotion, setCurrentEmotion] = useState<string | null>(null);
  const [currentEmoji, setCurrentEmoji] = useState<string | null>(null);
  const [currentEmotionPercentage, setCurrentEmotionPercentage] = useState<string | null>(null);
  const [dominantEmotion, setDominantEmotion] = useState<string | null>(null);
  const [dominantEmoji, setDominantEmoji] = useState<string | null>(null);
  const [dominantEmotionPercentage, setDominantEmotionPercentage] = useState<string | null>(null);
  const [emotionFrequencies, setEmotionFrequencies] = useState<{ [emotion: string]: number }>({});
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>(null);
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [merchantId, setMerchantId] = useState<string | null>(null);

  let recordingTimerInterval = useRef<NodeJS.Timeout | null>(null);

  // Ensure storage buckets are ready
  useEffect(() => {
    async function initStorage() {
      await ensureStorageBuckets();
    }
    initStorage();
  }, []);

  const handleStreamsReady = () => {
    setIsReadyToRecord(true);
  };

  const handleReadyToRecord = () => {
    setIsReadyToRecord(true);
  };

  const startRecordingTimer = () => {
    recordingTimerInterval.current = setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);
  };

  const stopRecordingTimer = () => {
    if (recordingTimerInterval.current) {
      clearInterval(recordingTimerInterval.current);
      recordingTimerInterval.current = null;
    }
  };

  const drawToCanvas = () => {
    if (!canvasRef.current || !mainVideoRef.current || !pipVideoRef.current) return;

    const canvas = canvasRef.current;
    const mainVideo = mainVideoRef.current;
    const pipVideo = pipVideoRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error("Couldn't get canvas context");
      return;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the main video
    ctx.drawImage(mainVideo, 0, 0, canvas.width, canvas.height);

    // Calculate PIP size and position
    const pipWidth = canvas.width * 0.3;  // 30% of canvas width
    const pipHeight = pipWidth * (pipVideo.videoHeight / pipVideo.videoWidth);
    const pipMargin = 20; // Margin from the edges

    const pipX = canvas.width - pipWidth - pipMargin;
    const pipY = canvas.height - pipHeight - pipMargin;

    // Draw the PIP video
    ctx.drawImage(pipVideo, pipX, pipY, pipWidth, pipHeight);

    // Request the next frame
    requestAnimationFrame(drawToCanvas);
  };

  // Get merchant ID from URL if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const merchantParam = urlParams.get('merchant');
    const pathSegments = window.location.pathname.split('/');
    
    // Check if we're on a merchant-specific recording page
    const isMerchantRecordingPath = pathSegments.length >= 3 && 
                                   pathSegments[1] === 'merchant' &&
                                   pathSegments[3] === 'record';
    
    if (isMerchantRecordingPath) {
      // Extract merchant ID from the URL path
      const merchantIdFromPath = pathSegments[2];
      console.log('Setting merchant ID from path:', merchantIdFromPath);
      setMerchantId(merchantIdFromPath);
    } else if (merchantParam) {
      console.log('Setting merchant ID from query param:', merchantParam);
      setMerchantId(merchantParam);
    }
  }, []);

  const saveRecordingAndSignIn = async () => {
    setIsSaving(true);
    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      if (!userId) {
        toast.error('Please sign in to save recordings');
        return;
      }

      // Convert video to blob
      if (!recordedVideo) {
        toast.error('No recording available to save');
        return;
      }

      const blob = await fetch(recordedVideo).then(r => r.blob());
      const timestamp = new Date().getTime();
      const sanitizedTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_') || 'recording';
      
      // Create user-specific folder structure
      const userFolder = `user_${userId}`;
      const fileName = `${timestamp}_${sanitizedTitle}.webm`;
      
      // Full path including user subfolder
      const filePath = `${userFolder}/${fileName}`;
      
      console.log(`Uploading video to path: ${filePath}`);
      const file = new File([blob], fileName, { type: 'video/webm' });
      
      // Ensure storage buckets exist before upload
      await ensureStorageBuckets();

      // Upload video to Supabase storage with user folder structure
      const { data, error } = await supabase
        .storage
        .from('videos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading video:', error);
        toast.error('Failed to upload video');
        setIsSaving(false);
        return;
      }

      console.log("Video upload succeeded:", data);

      // Get public URL of the video
      const { data: publicUrlData } = await supabase
        .storage
        .from('videos')
        .getPublicUrl(filePath);
      
      const videoUrl = publicUrlData?.publicUrl;
      
      if (!videoUrl) {
        console.error('Failed to get public URL for video');
        toast.error('Failed to get video URL');
        setIsSaving(false);
        return;
      }

      console.log('Video uploaded successfully at URL:', videoUrl);
      console.log('Merchant ID when saving:', merchantId);

      // Save recording metadata to Supabase database
      const recordingData = {
        user_id: userId,
        video_path: videoUrl,
        title: title || "Untitled Recording",
        details: description || null,
        merchant_id: merchantId, // Ensure we're passing the merchant ID
        emotion: {
          dominantEmotion: selectedEmotion || dominantEmotion,
          dominantEmoji: selectedEmoji || dominantEmoji,
          dominantEmotionPercentage: dominantEmotionPercentage
        },
        created_at: new Date().toISOString()
      };

      console.log("Saving recording metadata:", recordingData);

      // Insert into recordings table
      const { data: recordingResponse, error: dbError } = await supabase
        .from('recordings')
        .insert(recordingData)
        .select();

      if (dbError) {
        console.error('Error saving recording metadata:', dbError);
        toast.error('Failed to save recording metadata');
        setIsSaving(false);
        return;
      }

      // If this is a merchant recording and merchant_videos table exists, save to it as well
      if (merchantId && recordingResponse && recordingResponse[0]) {
        try {
          const recordingId = recordingResponse[0].id;
          
          await supabase
            .from('merchant_videos')
            .insert({
              merchant_id: merchantId,
              recording_id: recordingId
            });
            
          toast.success('Video recorded for merchant successfully!');
        } catch (merchantError) {
          console.error('Error saving merchant video relationship:', merchantError);
          // Don't fail the whole operation if just this part fails
        }
      } else {
        toast.success('Recording saved successfully!');
      }
      
      setRecordedVideo(null);
    } catch (error) {
      console.error('Error saving recording:', error);
      toast.error('Failed to save recording');
    } finally {
      setIsSaving(false);
    }
  };

  const shareToSocialMedia = async (platform: string) => {
    if (!recordedVideo || !dominantEmotion) return;

    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      if (!userId) {
        toast.error('Please sign in to share videos');
        return;
      }

      // Simplified version - normally would use a server-side upload service
      const shareUrl = recordedVideo;

      // Share to selected platform using our sharing service
      const { shareToSocialMedia } = await import('../../utils/socialSharingService');
      
      const success = await shareToSocialMedia(
        platform,
        shareUrl,
        title || 'Check out my video on GrinGauge!',
        description || `I'm feeling ${dominantEmotion}!`,
        userId,
        merchantId || undefined
      );
      
      if (success) {
        return true;
      } else {
        throw new Error(`Failed to share to ${platform}`);
      }

    } catch (error) {
      console.error('Error sharing to social media:', error);
      toast.error('Failed to share to social media');
      throw error;
    }
  };

  const shareToAllSocialMedia = async () => {
    const platforms = ['Facebook', 'Twitter', 'Instagram', 'YouTube'];
    let successCount = 0;
    
    for (const platform of platforms) {
      try {
        await shareToSocialMedia(platform);
        successCount++;
      } catch (error) {
        console.error(`Error sharing to ${platform}:`, error);
      }
    }
    
    if (successCount > 0) {
      toast.success(`Shared to ${successCount} platform${successCount > 1 ? 's' : ''}!`);
      return true;
    } else {
      toast.error('Failed to share to any platforms');
      return false;
    }
  };

  const startRecording = async () => {
    if (!mainVideoRef.current || !pipVideoRef.current || !canvasRef.current || isRecording) {
      return;
    }

    try {
      setIsRecording(true);
      setRecordingTime(0);

      // Get screen dimensions for proper canvas setup
      const mainVideo = mainVideoRef.current;
      const pipVideo = pipVideoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error("Couldn't get canvas context");
        return;
      }

      // Set up canvas dimensions for 9:16 vertical video with high quality
      canvas.width = 1080;  // Full HD width for vertical orientation
      canvas.height = 1920; // Full HD height for vertical orientation

      // Set up MediaRecorder with high quality options
      const canvasStream = canvas.captureStream(30); // 30fps for smoother video

      // Get audio from the user's microphone
      let audioStream;
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 2
          }
        });
      } catch (err) {
        console.error("Error getting audio stream:", err);
        // Continue without audio if audio capture fails
      }

      // Combine canvas video with audio
      let combinedStream;
      if (audioStream) {
        const audioTracks = audioStream.getAudioTracks();
        combinedStream = new MediaStream([...canvasStream.getVideoTracks(), ...audioTracks]);
      } else {
        combinedStream = canvasStream;
      }

      // Configure MediaRecorder with high quality settings
      const recorderOptions = {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 5000000, // 5 Mbps for high quality
        audioBitsPerSecond: 128000,  // 128 kbps audio
      };

      // Use a fallback MIME type if the browser doesn't support vp9
      if (!MediaRecorder.isTypeSupported(recorderOptions.mimeType)) {
        recorderOptions.mimeType = 'video/webm;codecs=vp8,opus';
        
        if (!MediaRecorder.isTypeSupported(recorderOptions.mimeType)) {
          // Use a more broadly supported codec as fallback
          recorderOptions.mimeType = 'video/webm';
        }
      }

      mediaRecorder.current = new MediaRecorder(combinedStream, recorderOptions);

      const recordedChunks: Blob[] = [];

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        stopRecordingTimer();
        const blob = new Blob(recordedChunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedVideo(url);
        setIsRecording(false);
      };

      // Start drawing frames to canvas and recording
      drawToCanvas();
      mediaRecorder.current.start(1000); // Collect data every second for better reliability
      
      // Start the recording timer
      startRecordingTimer();

    } catch (err) {
      console.error("Error starting recording:", err);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
  };

  const cancelRecording = () => {
    stopRecordingTimer();
    setIsRecording(false);
    setRecordedVideo(null);
    URL.revokeObjectURL(recordedVideo || '');
  };

  const handleEmotionDetected = (emotion: string, emoji: string, percentage: string | null) => {
    setCurrentEmotion(emotion);
    setCurrentEmoji(emoji);
    setCurrentEmotionPercentage(percentage);
  };

  const handleEmotionFrequencyUpdate = (frequencies: { [emotion: string]: number }) => {
    setEmotionFrequencies(frequencies);

    // Find the dominant emotion
    let dominantEmotion: string | null = null;
    let maxFrequency = 0;

    Object.keys(frequencies).forEach(emotion => {
      if (frequencies[emotion] > maxFrequency) {
        maxFrequency = frequencies[emotion];
        dominantEmotion = emotion;
      }
    });

    if (dominantEmotion) {
      setDominantEmotion(dominantEmotion);
      setDominantEmoji(getEmojiForEmotion(dominantEmotion));
      // Calculate and set the percentage
      const totalDetections = Object.values(frequencies).reduce((sum, frequency) => sum + frequency, 0);
      const percentage = ((frequencies[dominantEmotion] / totalDetections) * 100).toFixed(2);
      setDominantEmotionPercentage(`${percentage}%`);
    }
  };

  const selectAsDominantEmotion = (emotion: string, emojiUrl: string) => {
    setSelectedEmotion(emotion);
    setSelectedEmoji(emojiUrl);
  };

  return {
    mainVideoRef,
    pipVideoRef,
    canvasRef,
    isRecording,
    isReadyToRecord,
    recordingTime,
    recordedVideo,
    isSaving,
    currentEmotion,
    currentEmoji,
    currentEmotionPercentage,
    dominantEmotion,
    dominantEmoji,
    dominantEmotionPercentage,
    selectedEmotion,
    selectedEmoji,
    title,
    description,
    merchantId,
    setMerchantId,
    setTitle,
    setDescription,
    handleStreamsReady,
    handleReadyToRecord,
    startRecording,
    stopRecording,
    cancelRecording,
    handleEmotionDetected,
    handleEmotionFrequencyUpdate,
    selectAsDominantEmotion,
    shareToSocialMedia,
    shareToAllSocialMedia,
    saveRecordingAndSignIn
  };
};
